import { ChevronRightIcon } from 'lucide-preact';
import { useDispatch } from 'react-redux';

import { ButtonLink } from '@/components/Buttons';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { ParsedUserProduct } from '@/queries/products/types';

import PurchaseAgainButton from './PurchaseAgainButton';
import UserProductImage from './UserProductImage';
import { useUserProductDisplay } from './useUserProductDisplay';
import {
  getProductDescription,
  getProductPrice,
  getProductTitle,
  habitatIdsOf,
  isUserProductDetailsSupported,
} from './utils';

interface UserProductRowProps {
  userProduct: ParsedUserProduct;
  onSelect?: () => void;
}

const UserProductRow = ({ userProduct, onSelect }: UserProductRowProps) => {
  const source = userProduct.product?.source;
  const isBundling = source === 'bundling';
  const isGiftup = source === 'giftup';
  const isStripe = source === 'stripe';

  const { imageURL, title: displayTitle, variant, displayTitleName } = useUserProductDisplay(userProduct);

  const [now] = useLatestActiveTime();
  const isExpired = userProduct.validUntil < now;

  const title = getProductTitle(userProduct.product?.productLookupKey, userProduct.product?.name, displayTitleName);
  const description = getProductDescription(userProduct, now);
  const price = getProductPrice(userProduct.product?.price);

  const dispatch = useDispatch();
  const openSpeedyFlow = useOpenSpeedyFlow();

  const selectsDetailsModal = !isExpired && isUserProductDetailsSupported(userProduct);

  const stripePurchaseAgain =
    isStripe && isExpired && userProduct.eligibility?.allowed && userProduct.product?.productLookupKey;

  const purchaseAgainGiftupHref = isGiftup && isExpired ? '/gift' : undefined;
  const hidePriceInActions = isBundling || isGiftup;

  const actions = stripePurchaseAgain ? (
    <>
      <div className="flex items-center justify-end gap-4 mobile:hidden">
        <div>{price}</div>
        <PurchaseAgainButton userProduct={userProduct} />
      </div>
      <button
        type="button"
        className="preflight preflight-button flex items-center justify-center desktop:hidden"
        onClick={() => {
          // The purchase is already known, so the flow skips the product step and goes straight
          // to checkout. Without a price there is nothing to re-buy.
          const priceId = userProduct.product?.price?.priceId;
          if (!priceId) return;
          openSpeedyFlow({
            kind: 'habitat-access',
            habitatId: habitatIdsOf(userProduct)[0],
            product: {
              productId: userProduct.productId,
              priceId,
              associatedHabitatIds: habitatIdsOf(userProduct),
            },
            entryPoint: 'purchase-again',
          });
        }}
      >
        <span className="sr-only">View details</span>
        <ChevronRightIcon className="size-5" />
      </button>
    </>
  ) : purchaseAgainGiftupHref ? (
    <div className="flex items-center justify-end gap-2 desktop:gap-4">
      <ButtonLink
        href={purchaseAgainGiftupHref}
        className="shrink-0 items-center justify-center px-3 py-2 text-sm/none mobile:hidden desktop:px-5 desktop:py-3 desktop:text-base/none"
      >
        Purchase Again
      </ButtonLink>
      <a href={purchaseAgainGiftupHref} className="preflight preflight-a flex items-center justify-center desktop:hidden">
        <span className="sr-only">Purchase Again</span>
        <ChevronRightIcon className="size-5" />
      </a>
    </div>
  ) : selectsDetailsModal ? (
    <button type="button" className="preflight preflight-button flex items-center justify-center gap-3" onClick={onSelect}>
      {!hidePriceInActions ? <div className="mobile:hidden">{price}</div> : null}
      <ChevronRightIcon className="size-5" />
      <span className="sr-only">View purchase details</span>
    </button>
  ) : (
    <div className="mobile:hidden desktop:mr-5">{price}</div>
  );

  return (
    <div className="preflight flex items-center gap-3 py-4 desktop:py-2">
      <UserProductImage
        className="size-11 shrink-0 desktop:size-[60px]"
        imageURL={imageURL}
        title={displayTitle}
        variant={variant}
      />
      <div className="flex min-w-0 grow flex-col">
        <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">{title}</h6>
        <p className="preflight preflight-p line-clamp-1 text-base text-stone-500 desktop:text-xl">{description}</p>
      </div>
      <div className="text-base text-stone-500 desktop:text-xl">{actions}</div>
    </div>
  );
};

export default UserProductRow;
